import React, { useState, useRef, useEffect } from 'react';
import { matchSorter } from 'match-sorter';

interface ComboboxOption {
  id: number;
  name: string;
}

interface ComboboxInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: ComboboxOption[];
  label: string;
  placeholder: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
}

const highlightMatch = (text: string, query: string) => {
  if (!query) return text;
  
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, i) => 
    regex.test(part) ? 
      <span key={i} className="bg-red-500/30">{part}</span> : 
      <span key={i}>{part}</span>
  );
};

export const ComboboxInput: React.FC<ComboboxInputProps> = ({
  id,
  value,
  onChange,
  onBlur,
  options,
  label,
  placeholder,
  error,
  disabled,
  loading
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setInputValue(value); // Reset input to last valid value
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const filteredOptions = inputValue
    ? matchSorter(options, inputValue, { keys: ['name'] })
    : options;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: ComboboxOption) => {
    onChange(option.name);
    setInputValue(option.name);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
    }

    if (isOpen) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          setInputValue(value); // Reset to last valid value
          break;
      }
    }
  };

  const inputClasses = `mt-1 block w-full rounded-md border text-white placeholder-gray-400
    shadow-sm text-sm py-2 px-3 transition-colors
    ${disabled ? 'bg-gray-900 opacity-50' : 'bg-gray-800/90 hover:bg-gray-800 focus:bg-gray-800'}
    ${error ? 'border-red-500' : 'border-gray-700 hover:border-gray-600 focus:border-red-500 focus:ring-red-500'}`;

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={id} className="block text-sm font-medium text-gray-200">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            // Small delay to allow click events on options
            setTimeout(() => {
              if (onBlur) onBlur();
            }, 200);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={inputClasses}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`${id}-listbox`}
          aria-activedescendant={
            highlightedIndex >= 0 ? 
            `${id}-option-${highlightedIndex}` : 
            undefined
          }
        />
        {loading && (
          <div className="absolute right-2 top-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}

      {isOpen && filteredOptions.length > 0 && (
        <ul
          id={`${id}-listbox`}
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 border border-gray-700 shadow-lg"
          role="listbox"
        >
          {filteredOptions.map((option, index) => (
            <li
              key={option.id}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`cursor-pointer px-4 py-2 text-sm text-gray-200
                ${highlightedIndex === index ? 'bg-gray-700' : 'hover:bg-gray-700'}
                transition-colors duration-150`}
              onClick={() => handleOptionSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {highlightMatch(option.name, inputValue)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
